import {ConfirmDialog} from "primereact/confirmdialog";
import {Message} from "primereact/message";
import {TabPanel, TabView} from "primereact/tabview";
import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {shallowEqual, useDispatch, useSelector} from "react-redux";

import {EnergyValueOutput} from "../../app/value/TabEnergyValueIndication";
import AppreciationTabInputText from "../../component/appreciation/AppreciationTabInputText";
import DocumentUploader from "../../component/DocumentUploader";
import I18nErrorMessage from "../../component/I18nErrorMessage";
import LoadingIndicator from "../../component/LoadingIndicator";
import {TabHeaderWithTooltip} from "../../component/TabHeaderWithTooltip";
import ValueLendingValueInfo from "../../component/ValueLendingValueInfo";
import {useCheckPermission, useCheckPermissionAdmin} from "../../hooks/permissionHooks";
import {useHeightToViewportBottom} from "../../hooks/useHeightToViewportBottom";
import {
    selectInitialized as documentsInitialized,
    selectDocuments,
    selectDocumentsByType,
    selectLoading as selectDocumentsLoading,
} from "../../redux/documentsSlice";
import {downloadDocumentAsync, loadDocumentsAsync} from "../../redux/documentsThunk";
import {
    clearIdpEnergy,
    extractIdpEnergyAsync,
    selectIdpEnergyError,
    selectIdpEnergyLoading,
    selectIdpEnergyValues,
} from "../../redux/idpEnergySlice";
import {selectVisibleObjectParametersCurried} from "../../redux/objectParametersSelectors";
import {
    selectAllEnergyParams,
    selectAllIdpEnergyCertParams,
    setParamsForParameterType,
    setSingleComment,
} from "../../redux/objectParametersSlice";
import {validateAndSubmitThunk} from "../../redux/objectParametersThunk";
import {selectPersistenceLoading, selectRevaId} from "../../redux/persistenceSlice";
import {persistMultipleParameters} from "../../redux/persistenceThunk";
import {selectLoading as selectSpecLoading} from "../../redux/specSlice";
import {selectError} from "../../redux/valueIndicationSlice";
import {
    energyCertEmptyKeysToReset,
    needsReplaceConfirm,
    showMultipleCertificatesWarning,
} from "../../util/energyCertificateHints";
import {PERMISSION_RULE_IDP_ENERGY} from "../../util/permissions";
import IdpEnergyRerunButton from "./IdpEnergyRerunButton";
import {ObjectInputModernizationDegree} from "./ObjectInputModernizationDegree";
import {CollapsableSection, ObjectInputsBlock, SectionTitle} from "./ObjectInputs";
import PersistenceFinalMessage from "./PersistenceFinalMessage";
import SnapshotMessage from "./SnapshotMessage";

// Orders-API-Dokumenttyp eines Energieausweises beim LESEN (Deutsch); der Upload nutzt den englischen
// Typ "EnergyCertificate" (s. DocumentUploader unten).
const ENERGY_CERTIFICATE_DOCUMENT_TYPE = "Energieausweis";

/**
 * Lädt ein einzelnes Energieausweis-Dokument herunter und zeigt es als PDF-iframe bzw. Bild an. Das iframe
 * reicht bis zum unteren Fensterrand (#19096).
 * Die Object-URL wird beim Unmount/Dokumentwechsel wieder freigegeben.
 */
function EnergyCertificatePreview({document}) {
    const dispatch = useDispatch();
    const [url, setUrl] = useState(undefined);
    const frameRef = useRef(null);
    const isPdf = document.extension?.toLowerCase() === "pdf";
    const frameHeight = useHeightToViewportBottom(frameRef, isPdf && url != null);

    useEffect(() => {
        let objectUrl;
        let cancelled = false;
        dispatch(downloadDocumentAsync(document))
            .then((blob) => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setUrl(objectUrl);
            })
            .catch(console.error);
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [document, dispatch]);

    if (!url) return null;
    const extension = document.extension?.toLowerCase();
    if (isPdf)
        return (
            <iframe
                ref={frameRef}
                title={document.filename}
                src={url}
                width="100%"
                height={frameHeight}
                style={{marginTop: "1rem", display: "block"}}
            />
        );
    if (extension && /png|jpe?g|avif/.test(extension))
        return <img alt={document.filename} src={url} width="100%" style={{marginTop: "1rem"}} />;
    return null;
}

/**
 * Zeigt ALLE Energieausweise der Bewertung an. Bei mehreren wird pro Energieausweis ein Sub-Tab
 * (Beschriftung = Dateiname) gerendert, damit der Nutzer alle einsehen kann; bei genau einem die
 * Vorschau direkt ohne Tab-Rahmen. (Auswahl-/Warn-Logik bei mehreren EAusweisen folgt separat, #19166.)
 */
/**
 * Warn-Banner im Energieausweis-Tab, wenn mehrere Energieausweise vorliegen und die IDP-Schicht noch leer ist
 * (keine Auto-Extraktion gelaufen) — der Nutzer muss sich für einen entscheiden und ihn auswerten. Verschwindet, sobald
 * ein Energieausweis ausgewertet wurde (IDP-Schicht gefüllt). Ergänzt den Badge am Objekt-Tab (#19166).
 * Nur mit reva-idp-energy: ohne das Recht wird nie ein Ausweis ausgewertet (#19302 K6-B5).
 */
function MultipleEnergyCertificatesWarning() {
    const {t} = useTranslation();
    const certificates = useSelector(
        (state) => selectDocumentsByType(state, ENERGY_CERTIFICATE_DOCUMENT_TYPE),
        shallowEqual,
    );
    const idpParams = useSelector(selectAllIdpEnergyCertParams);
    const idpLayerEmpty = !idpParams || Object.keys(idpParams).length === 0;
    const canUseIdpEnergy = useCheckPermission(PERMISSION_RULE_IDP_ENERGY);

    if (!showMultipleCertificatesWarning(certificates.length, idpLayerEmpty, canUseIdpEnergy)) return null;

    return (
        <Message
            severity="warn"
            className="w-full justify-content-start mb-4"
            text={t("object.energy.certificate.multipleWarning")}
        />
    );
}

function EnergyCertificateDocument({activeIndex, onTabChange}) {
    const {t} = useTranslation();
    const certificates = useSelector(
        (state) => selectDocumentsByType(state, ENERGY_CERTIFICATE_DOCUMENT_TYPE),
        shallowEqual,
    );
    // Dateiname des tatsächlich ausgewerteten Energieausweises (in der IDP-Persistenzschicht vermerkt).
    const idpParams = useSelector(selectAllIdpEnergyCertParams);
    const evaluatedFileName = idpParams?.energy_certificate_file_name;

    if (!certificates || certificates.length === 0) return null;

    if (certificates.length === 1) return <EnergyCertificatePreview document={certificates[0]} />;

    // Bei mehreren Energieausweisen: pro EA ein Sub-Tab (Beschriftung = Dateiname). Der aktive Tab wird vom
    // Eltern-Panel kontrolliert (damit der Rerun-Button denselben EA trifft). Der EA, dessen Daten aktuell in
    // der IDP-Schicht liegen, wird zusätzlich zur normalen Aktiv-Tab-Hervorhebung visuell markiert (Haken-Icon
    // + fetter Dateiname). Der Abstand nach oben (mt-6) trennt die Sub-Tab-Header von der Schaltflächen-Zeile.
    return (
        <div className="mt-6">
            <TabView
                activeIndex={Math.min(activeIndex ?? 0, certificates.length - 1)}
                onTabChange={(e) => onTabChange?.(e.index)}
            >
                {certificates.map((certificate) => {
                    const isEvaluated = !!evaluatedFileName && certificate.filename === evaluatedFileName;
                    return (
                        <TabPanel
                            key={certificate.documentId}
                            headerClassName={isEvaluated ? "energyCertificateEvaluatedTab" : undefined}
                            header={
                                <span className="flex items-center gap-2">
                                    {isEvaluated && (
                                        <i
                                            className="pi pi-check-circle"
                                            style={{color: "var(--primary-color)"}}
                                            title={t("object.energy.certificate.evaluatedHint")}
                                        />
                                    )}
                                    <span style={isEvaluated ? {fontWeight: 600} : undefined}>
                                        {certificate.filename}
                                    </span>
                                </span>
                            }
                        >
                            <EnergyCertificatePreview document={certificate} />
                        </TabPanel>
                    );
                })}
            </TabView>
        </div>
    );
}

/**
 * Takes over the values extracted from the uploaded energy certificate via IDP into the
 * IDP_ENERGY_CERTIFICATE parameter layer automatically, as soon as a result arrives — no confirmation
 * dialog, no partial/manual take-over. A manually maintained ENERGY_CERT value always overrides
 * the IDP-derived one (see resolveActiveParameterValue's ENERGY_CERT fallback).
 * @returns {null}
 */
function IdpResultEnergy() {
    const dispatch = useDispatch();

    const loading = useSelector(selectIdpEnergyLoading);
    const values = useSelector(selectIdpEnergyValues);
    const error = useSelector(selectIdpEnergyError);
    const storedIdpParams = useSelector(selectAllIdpEnergyCertParams);
    const energyParams = useSelector(selectAllEnergyParams);

    useEffect(() => {
        if (loading || error || values == null) return;

        // Ein leeres Ergebnis (nichts extrahiert / HTTP 204) darf eine zuvor persistierte IDP-Schicht NICHT
        // löschen -> kein Write, nur aufräumen.
        if (Object.keys(values).length === 0) {
            dispatch(clearIdpEnergy());
            return;
        }

        // Full-Replace der IDP_ENERGY_CERTIFICATE-Fallback-Schicht (identisch zum serverseitigen Auto-Trigger, der
        // die Spalte komplett ersetzt): Der Client-State wird gesetzt (nicht gemerged); für die Persistenz
        // werden außer den neuen Werten die zuvor vorhandenen, nun nicht mehr gelieferten Keys explizit auf
        // null gesetzt, damit der Delta-(PATCH-)Endpunkt sie entfernt (null => remove). Die IDP-Keys sind
        // bereits REVA-Parameter-Keys mit REVA-fertigen Werten (ISO-Datum, categoryNumber, Zahlen —
        // Normalisierung macht der /energy-certificate-Endpunkt); es werden ALLE gelieferten Keys übernommen
        // (kein Filtern auf Segment-Inputs), Nicht-Segment-Keys sind harmlos (nur Fallback-Schicht).
        // Persistiert wird unbedingt (nicht validity-gegatet), damit die Übernahme nicht an einem
        // unabhängigen Formularfehler scheitert. Diese Komponente wird nur für Admins gemountet, daher ist
        // der Log nicht für normale Nutzer sichtbar.
        console.log("[IdpEnergy] Ergebnis uebernommen (IDP_ENERGY_CERTIFICATE, Replace)", values);

        const persistPayload = {...values};
        Object.keys(storedIdpParams ?? {}).forEach((key) => {
            if (!(key in values)) persistPayload[key] = null; // Alt-Key aus der Schicht entfernen
        });

        // #19774: „Keine Angabe“ in ENERGY_CERT für die neu ausgelesenen Parameter zurücksetzen, damit der Wert greift.
        const energyKeysToReset = energyCertEmptyKeysToReset(energyParams, values);
        if (energyKeysToReset.length > 0) {
            const resetParams = Object.fromEntries(energyKeysToReset.map((key) => [key, null]));
            dispatch(
                setParamsForParameterType({parameterType: "ENERGY_CERT", params: {...energyParams, ...resetParams}}),
            );
            // Der PATCH ohne Kommentar entfernt serverseitig auch die Begründung zu „Keine Angabe“; lokal ebenso.
            for (const key of energyKeysToReset) {
                dispatch(setSingleComment({parameter: key, parameterType: "ENERGY_CERT", comment: null}));
            }
            dispatch(persistMultipleParameters(resetParams, "ENERGY_CERT"));
        }

        dispatch(setParamsForParameterType({parameterType: "IDP_ENERGY_CERTIFICATE", params: values}));
        dispatch(persistMultipleParameters(persistPayload, "IDP_ENERGY_CERTIFICATE"));
        dispatch(validateAndSubmitThunk());
        dispatch(clearIdpEnergy());
    }, [loading, error, values, storedIdpParams, energyParams, dispatch]);

    return null;
}

function EnergyInputPanel() {
    const specLoading = useSelector(selectSpecLoading);
    const persistenceLoading = useSelector(selectPersistenceLoading);
    const loading = specLoading || persistenceLoading;
    const columns = useSelector(selectVisibleObjectParametersCurried("object/energy", "all"));
    const blocks = columns?.[0]?.blocks ?? [];
    const {t} = useTranslation();

    return (
        <div className="flex-1">
            <LoadingIndicator loading={loading} className="h-full" />
            {!loading && (
                <>
                    <SectionTitle
                        title={t("object.energy.input.title.property_age")}
                        firstSection={true}
                        collapseKey="energy_property_age"
                    />
                    <CollapsableSection collapseKey="energy_property_age">
                        {blocks[0] && <ObjectInputsBlock block={blocks[0]} />}
                        <div className="element-panel mb-4">
                            <ObjectInputModernizationDegree heatingOnly />
                        </div>
                    </CollapsableSection>

                    <SectionTitle
                        title={t("object.energy.input.title.energetic_properties")}
                        collapseKey="energy_energetic_properties"
                    />
                    <CollapsableSection collapseKey="energy_energetic_properties">
                        {blocks[1] && <ObjectInputsBlock block={blocks[1]} />}
                    </CollapsableSection>
                </>
            )}
        </div>
    );
}

/**
 * Admin-only (Debug): zeigt die rohe IDP-Extraktion an, wie sie in der IDP-Persistenzschicht
 * (IDP_ENERGY_CERTIFICATE) liegt — als eingerücktes Plain-JSON. Rein zur Kontrolle/Nachvollziehbarkeit
 * der Auslesung; bleibt bewusst Admins-only (s. Tab-Header-Beschriftung), unabhängig von reva-idp-energy.
 */
function EnergyCertificateDataView() {
    const {t} = useTranslation();
    const idpParams = useSelector(selectAllIdpEnergyCertParams);
    const hasData = idpParams && Object.keys(idpParams).length > 0;

    if (!hasData) return <p style={{marginTop: "1rem"}}>{t("object.energy.output.energy_data_empty")}</p>;

    return (
        <pre style={{marginTop: "1rem", whiteSpace: "pre-wrap", wordBreak: "break-word"}}>
            {JSON.stringify(idpParams, null, 2)}
        </pre>
    );
}

function EnergieOutputPanel() {
    // Seit #19381 gibt es nur noch die Wertindikation; ihr Fehler ist zugleich der des Energiekennwerts.
    const indicationError = useSelector(selectError);
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const [activeIndex, setActiveIndex] = useState(0);
    // IDP-Feature (Auto-Trigger beim Upload, Rerun-Button, Ergebnis-Übernahme): reva-idp-energy
    // (Admins implizit). Der Debug-Tab "Energieausweis-Daten (Admins-only)" bleibt dagegen Admins-only.
    const canUseIdpEnergy = useCheckPermission(PERMISSION_RULE_IDP_ENERGY);
    const isAdmin = useCheckPermissionAdmin();

    // Bestehende Dokumente der Bewertung — der Orders-API-Typ eines Energieausweises ist "Energieausweis"
    // (Deutsch), während der Upload den englischen Typ "EnergyCertificate" nutzt.
    const documents = useSelector(selectDocuments);
    const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);
    const confirmResolveRef = useRef(null);

    // Auswahl des aktiven Energieausweis-Sub-Tabs wird hier gehalten, damit der Rerun-Button auf genau dem
    // gewählten EA arbeitet (nicht mehr fix auf dem ersten).
    const energyCertificates = useSelector(
        (state) => selectDocumentsByType(state, ENERGY_CERTIFICATE_DOCUMENT_TYPE),
        shallowEqual,
    );
    const [activeCertificateIndex, setActiveCertificateIndex] = useState(0);
    const safeCertificateIndex =
        energyCertificates.length > 0 ? Math.min(activeCertificateIndex, energyCertificates.length - 1) : 0;
    const selectedCertificate = energyCertificates[safeCertificateIndex];

    // Beim (Neu-)Betreten des Tabs bei >=2 Energieausweisen einmalig den ausgewerteten EA (dessen Daten in der
    // IDP-Schicht liegen) als aktiven Sub-Tab vorauswählen. Danach nicht mehr eingreifen, damit weder eine
    // manuelle Tab-Wahl noch ein Rerun (der den ausgewerteten EA wechselt) die Auswahl überschreibt.
    const idpParams = useSelector(selectAllIdpEnergyCertParams);
    const didPreselectCertificate = useRef(false);
    useEffect(() => {
        if (didPreselectCertificate.current || energyCertificates.length < 2) return;
        const evaluatedFileName = idpParams?.energy_certificate_file_name;
        if (!evaluatedFileName) return;
        const index = energyCertificates.findIndex((certificate) => certificate.filename === evaluatedFileName);
        if (index >= 0) setActiveCertificateIndex(index);
        didPreselectCertificate.current = true;
    }, [energyCertificates, idpParams]);

    // Rückfrage vor dem Upload: existiert bereits ein Energieausweis, muss der Upload eines weiteren
    // (der die bestehenden IDP-Ergebnisse ersetzt) blockierend bestätigt werden.
    // Ohne reva-idp-energy gibt es keine IDP-Ergebnisse zu ersetzen (#19302 K6-B6).
    const confirmCertificateUpload = () => {
        const alreadyHasCertificate = (documents ?? []).some(
            (doc) => doc.documentType === ENERGY_CERTIFICATE_DOCUMENT_TYPE,
        );
        if (!needsReplaceConfirm(alreadyHasCertificate, canUseIdpEnergy)) {
            return true;
        }
        return new Promise((resolve) => {
            confirmResolveRef.current = resolve;
            setShowReplaceConfirm(true);
        });
    };

    const resolveReplaceConfirm = (accepted) => {
        setShowReplaceConfirm(false);
        const resolve = confirmResolveRef.current;
        confirmResolveRef.current = null;
        resolve?.(accepted);
    };

    // Nach erfolgreichem Upload eines PDF-Energieausweises automatisch die IDP-Extraktion anstoßen.
    // Bilder (png/jpg/...) unterstützt der IDP-Energy /energy-certificate-Endpunkt nicht -> kein Call.
    const onCertificateUploaded = (file) => {
        if (canUseIdpEnergy && file && /\.pdf$/i.test(file.name)) {
            dispatch(extractIdpEnergyAsync(file));
        }
    };
    if (indicationError) {
        return (
            <>
                <h3 className="tabViewTitle">{t("object.energy.output.tab_header.preview.title")}</h3>
                <I18nErrorMessage i18nKey={indicationError.error} />
                <I18nErrorMessage errorObject={indicationError} />
            </>
        );
    }
    return (
        <TabView className="energyOutputTabView" activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
            <TabPanel
                key="outputPanel"
                header={<TabHeaderWithTooltip i18nPrefix="object.energy.output.tab_header.preview" />}
            >
                <EnergyValueOutput variant="preview" />
            </TabPanel>
            <TabPanel
                key="uploadPanel"
                header={<span className="tabViewTitle">{t("object.energy.output.tab_header.energy_upload")}</span>}
            >
                <div className="flex items-center gap-4">
                    <h3 className="m-0">{t("object.energy.input.upload")}</h3>
                    <DocumentUploader
                        documentType={"EnergyCertificate"}
                        onUploaded={onCertificateUploaded}
                        confirmBeforeUpload={confirmCertificateUpload}
                    />
                    {canUseIdpEnergy && <IdpEnergyRerunButton document={selectedCertificate} />}
                </div>
                <ConfirmDialog
                    visible={showReplaceConfirm}
                    header={t("object.energy.upload.replaceConfirm.header")}
                    message={t("object.energy.upload.replaceConfirm.message")}
                    icon="pi pi-exclamation-triangle"
                    acceptLabel={t("general.ok")}
                    rejectLabel={t("general.cancel")}
                    accept={() => resolveReplaceConfirm(true)}
                    reject={() => resolveReplaceConfirm(false)}
                    onHide={() => resolveReplaceConfirm(false)}
                />
                {canUseIdpEnergy && <IdpResultEnergy />}
                <EnergyCertificateDocument activeIndex={safeCertificateIndex} onTabChange={setActiveCertificateIndex} />
            </TabPanel>
            {isAdmin && (
                <TabPanel
                    key="dataPanel"
                    header={<span className="tabViewTitle">{t("object.energy.output.tab_header.energy_data")}</span>}
                >
                    <EnergyCertificateDataView />
                </TabPanel>
            )}
        </TabView>
    );
}

/**
 * @returns {React.JSX.Element}
 */
function TabObjectEnergy() {
    const revaId = useSelector(selectRevaId);
    const isDocumentsInitialized = useSelector(documentsInitialized);
    const isDocumentsLoading = useSelector(selectDocumentsLoading);
    const dispatch = useDispatch();

    useEffect(() => {
        if (revaId && !isDocumentsInitialized && !isDocumentsLoading) dispatch(loadDocumentsAsync());
    }, [revaId, isDocumentsInitialized, isDocumentsLoading, dispatch]);

    // Beim Wechsel der Bewertung ein evtl. vorhandenes IDP-Ergebnis verwerfen, damit kein
    // Ergebnis aus einer anderen Bewertung hängen bleibt.
    useEffect(() => {
        dispatch(clearIdpEnergy());
    }, [revaId, dispatch]);

    return (
        <div id="EnergyPanelTitle">
            <PersistenceFinalMessage />
            <SnapshotMessage />
            <MultipleEnergyCertificatesWarning />
            <AppreciationTabInputText page="object" tab="energy" />
            <ValueLendingValueInfo />

            <div className="grid grid-cols-12 gap-4">
                <div className="col-span-6">
                    <EnergyInputPanel />
                </div>
                <div className="col-span-6">
                    <EnergieOutputPanel />
                </div>
            </div>
        </div>
    );
}

export default TabObjectEnergy;
